import { useMemo, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/useAuth';
import { useI18n } from '../context/useI18n';
import api from '../services/api';
import {
  getServicesFor,
  getD28dProgramId,
  isD28dOnlyFinalUser,
  isTrainingOnlyFinalUser,
  isCoachClientUser,
  isVirtualGymMember,
  hideFoodForGymVirtualConsumer,
} from './dashboard/userServices';
import { PUBLIC_BRAND_NAME, isCoachWhiteLabelActive, mergeCoachBrand } from '../utils/branding';
import { useFrontendConfig } from '../context/FrontendConfigContext';
import { openFoodModule, isFoodExternal } from '../utils/foodModule';
import { isTrainingExternal, openTrainingModule } from '../utils/trainingModule';
import { isConsumerAccessBlocked } from '../utils/subscriptionAccess';
import { isSedeFinalUser } from '../utils/gymRoleScope';
import { canAccessFitLive } from '../utils/fitLiveModule';
import { fitLiveApi } from '../fit-live/api';
import PendingPaymentBanner from './dashboard/PendingPaymentBanner';
import D28dProgressDashboard from './d28d/D28dProgressDashboard';
import D28dChallengesPanel from './d28d/D28dChallengesPanel';
import FitnessTestPanel from './d28d/FitnessTestPanel';
import TrainingProgressPanel from './training/TrainingProgressPanel';
import AthleteTrainingEvolutionChart from './training/AthleteTrainingEvolutionChart';
import D28dAthleteEvolutionChart from './d28d/D28dAthleteEvolutionChart';
import ProgressPhotosPanel from './d28d/ProgressPhotosPanel';
import TrainingBodyMeasurements from './training/TrainingBodyMeasurements';
import TrainingWellnessPanel from './training/TrainingWellnessPanel';
import TrainingFoodSyncBanner from './training/TrainingFoodSyncBanner';
import SharedProgressSnapshot from './training/SharedProgressSnapshot';
import TrainingModule from './TrainingModule';
import LiveClassSchedule from './LiveClassSchedule';
import './LiveClasses.css';

function TrainingCoachCard({ trainerId }) {
  const { t } = useI18n();
  const [trainer, setTrainer] = useState(null);
  useEffect(() => {
    if (!trainerId) return;
    let active = true;
    api.get(`/trainers/${trainerId}`).then((r) => {
      if (active) setTrainer(r.data?.data || r.data || null);
    }).catch(() => { if (active) setTrainer(null); });
    return () => { active = false; };
  }, [trainerId]);
  if (!trainer) return null;
  return (
    <section className="card p-5">
      <h2 className="text-lg font-bold text-[var(--d28d-text)] mb-2">{t('mi_progreso.coach_title', 'Mi coach')}</h2>
      <p className="font-semibold text-[var(--d28d-text)]">{trainer.brand_name || trainer.nombre}</p>
      {trainer.especialidad ? <p className="text-sm d28d-text-muted">{trainer.especialidad}</p> : null}
      {trainer.welcome_message ? <p className="text-sm d28d-text-muted mt-2">{trainer.welcome_message}</p> : null}
    </section>
  );
}

const TAB_ALIASES = {
  resumen: 'general',
  habitos: 'alimentacion',
  premiacion: 'premiacion',
};

const TABS = [
  { id: 'general' },
  { id: 'alimentacion' },
  { id: 'entrenamiento' },
  { id: 'clases' },
  { id: 'medidas', labelKey: 'fotos' },
  { id: 'pruebas' },
  { id: 'premiacion' },
];

function normalizeTab(tab) {
  return TAB_ALIASES[tab] || tab;
}

export default function MiProgreso({
  initialTab = 'general',
  onTabChange,
  onOpenAccount,
  onNavigate,
  brandName = '',
  coachWhiteLabel = false,
  initialTrainingSubview = null,
  gymBrand = null,
}) {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const { config: frontendConfig } = useFrontendConfig();
  const [tab, setTab] = useState(normalizeTab(initialTab));
  const [trainingSubview, setTrainingSubview] = useState(initialTrainingSubview);
  const [foodError, setFoodError] = useState('');
  const [foodLoading, setFoodLoading] = useState(false);
  const [progressRefreshKey, setProgressRefreshKey] = useState(0);
  const [fitLiveSnap, setFitLiveSnap] = useState(null);
  const [fitLiveLoading, setFitLiveLoading] = useState(false);

  const handleOpenFood = async () => {
    setFoodLoading(true);
    setFoodError('');
    await openFoodModule('/dashboard', { user, myServices: services });
    setFoodLoading(false);
  };

  useEffect(() => {
    if (initialTrainingSubview) setTrainingSubview(initialTrainingSubview);
  }, [initialTrainingSubview]);

  useEffect(() => {
    const bump = () => setProgressRefreshKey((n) => n + 1);
    window.addEventListener('d28d:progress:refresh', bump);
    return () => window.removeEventListener('d28d:progress:refresh', bump);
  }, []);

  const setActiveTab = (next) => {
    const resolved = normalizeTab(next);
    setTab(resolved);
    onTabChange?.(resolved);
  };

  const services = useMemo(
    () => getServicesFor(user, frontendConfig, lang),
    [user, frontendConfig, lang],
  );

  const hasFood = !hideFoodForGymVirtualConsumer(user, gymBrand)
    && services.some((s) => s.id === 'food-plan');
  const hasD28d = services.some((s) => s.id === 'd28d' || s.id === 'live-classes');
  const hasTraining = services.some((s) => s.id === 'training');
  const hasFitLive = canAccessFitLive(user) && (
    services.some((s) => s.id === 'fit-live')
    || Boolean(user?.module_access?.fit_live || user?.moduleAccess?.fit_live)
  );
  const d28dOnly = isD28dOnlyFinalUser(services);
  const trainingOnly = isTrainingOnlyFinalUser(services);

  const refreshFitLiveProgress = useCallback(async () => {
    if (!hasFitLive) {
      setFitLiveSnap(null);
      return;
    }
    setFitLiveLoading(true);
    try {
      const [w, mine] = await Promise.all([
        fitLiveApi.walletMe().catch(() => null),
        fitLiveApi.myReservations().catch(() => ({ reservations: [] })),
      ]);
      const reservations = Array.isArray(mine?.reservations) ? mine.reservations : [];
      const active = reservations.filter((r) => ['held', 'confirmed', 'attended'].includes(r.status)).length;
      setFitLiveSnap({
        balance: Number(w?.wallet?.balance ?? 0),
        reservations: reservations.length,
        active,
        updatedAt: Date.now(),
      });
    } catch {
      setFitLiveSnap((prev) => prev || { balance: 0, reservations: 0, active: 0, updatedAt: Date.now() });
    } finally {
      setFitLiveLoading(false);
    }
  }, [hasFitLive]);

  useEffect(() => {
    if (tab !== 'general') return;
    refreshFitLiveProgress();
  }, [refreshFitLiveProgress, progressRefreshKey, tab]);

  useEffect(() => {
    if (!hasFitLive) return undefined;
    // Solo al volver a la pestaña del navegador (no en cada focus de ventana: evita refetch en bucle).
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        setProgressRefreshKey((n) => n + 1);
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [hasFitLive]);
  const coachClient = isCoachClientUser(user);
  const coachWl = coachWhiteLabel || isCoachWhiteLabelActive(user, mergeCoachBrand(user, null));
  const isSedeFinal = isSedeFinalUser(user, gymBrand);
  const hideD28dBranding = coachWl || (coachClient && !hasD28d) || isSedeFinal;
  const programId = getD28dProgramId(user);
  const virtualGymMember = isVirtualGymMember(user);
  const foodExternal = hasFood && isFoodExternal();
  const trainingExternal = hasTraining && isTrainingExternal();
  const accessBlocked = isConsumerAccessBlocked(user);

  useEffect(() => {
    const resolved = normalizeTab(initialTab);
    setTab(resolved);
    if (resolved === 'general' && hasD28d) {
      setProgressRefreshKey((n) => n + 1);
    }
  }, [initialTab, hasD28d]);

  useEffect(() => {
    if (tab === 'general' && hasD28d) {
      setProgressRefreshKey((n) => n + 1);
    }
  }, [tab, hasD28d]);

  const showD28dProgressUi = hasD28d || isSedeFinal;
  const showD28dTests = hasD28d && !isSedeFinal && (virtualGymMember || !hideD28dBranding);
  const showD28dPhotos = showD28dTests || (hasD28d && virtualGymMember && !isSedeFinal);
  const showD28dExtras = hasD28d && !d28dOnly && !isSedeFinal && (virtualGymMember || !hideD28dBranding);

  const visibleTabs = TABS.filter((tb) => {
    if (tb.id === 'alimentacion') return hasFood;
    if (tb.id === 'entrenamiento') return hasTraining;
    if (tb.id === 'clases') return hasD28d || isSedeFinal;
    if (tb.id === 'medidas') return hasTraining || hasD28d;
    if (tb.id === 'pruebas') return showD28dTests && !isSedeFinal;
    if (tb.id === 'premiacion') return showD28dExtras;
    return true;
  });

  useEffect(() => {
    if (!visibleTabs.some((tb) => tb.id === tab)) {
      setActiveTab('general');
    }
  }, [visibleTabs, tab]);

  const subtitle = useMemo(() => {
    if (isSedeFinal) {
      return t(
        'mi_progreso.subtitle_sede',
        'Tu asistencia, inscripciones y clases en vivo de {brand}.',
        { brand: brandName || gymBrand?.brand_name || gymBrand?.nombre || t('mi_progreso.your_gym', 'tu sede') },
      );
    }
    if (trainingOnly && hideD28dBranding) {
      return t(
        'mi_progreso.subtitle_training_brand',
        'Tu rutina, medidas y progreso con {brand}.',
        { brand: brandName || t('mi_progreso.your_coach', 'tu entrenador') },
      );
    }
    if (d28dOnly) {
      return t('mi_progreso.subtitle_d28d', 'Tu avance, clases en vivo e inscripciones en un solo lugar.');
    }
    if (hideD28dBranding) {
      return t(
        'mi_progreso.subtitle_coach',
        'Tu avance en nutrición y entrenamiento con {brand}.',
        { brand: brandName || t('mi_progreso.your_coach', 'tu entrenador') },
      );
    }
    return t('mi_progreso.subtitle', 'Tu avance en nutrición, entrenamiento y Fit Live · Programa en un solo lugar.');
  }, [trainingOnly, d28dOnly, hideD28dBranding, isSedeFinal, brandName, gymBrand, t]);

  return (
    <div className="dashboard-main-view surface-readable space-y-6">
      <header>
        <h1 className="d28d-page-title text-2xl">
          {t('nav.mi_progreso', 'Mi Progreso')}
        </h1>
        <p className="d28d-text-muted text-sm mt-1">{subtitle}</p>
      </header>

      {accessBlocked ? (
        <PendingPaymentBanner />
      ) : (
        <>

      <div className="live-classes-panel__tabs border-b border-[var(--d28d-border)] pb-2" role="tablist">
        {visibleTabs.map((tb) => (
          <button
            key={tb.id}
            type="button"
            role="tab"
            aria-selected={tab === tb.id}
            className={`live-classes-panel__tab ${tab === tb.id ? 'live-classes-panel__tab--active' : ''}`}
            onClick={() => setActiveTab(tb.id)}
          >
            {tb.id === 'medidas' && hasD28d && !hasTraining
              ? t('mi_progreso.tab.fotos', 'Fotos')
              : t(`mi_progreso.tab.${tb.labelKey || tb.id}`, tb.id)}
          </button>
        ))}
      </div>

      {tab === 'general' && (
        <div className="space-y-6">
          <div className="card p-4 space-y-3">
            <h2 className="font-bold text-[var(--d28d-text)] text-sm uppercase tracking-wide">
              {t('mi_progreso.results_title', 'Tu actividad')}
            </h2>
            <p className="text-sm d28d-text-muted">
              {isSedeFinal
                ? t(
                  'mi_progreso.results_hint_sede',
                  'Asistidos (Zoom), días del plan e inscripciones. El detalle de clases está en la pestaña Clases.',
                )
                : t(
                  'mi_progreso.results_hint',
                  'Asistidos (Zoom), días del plan e inscripciones. Pruebas y fotos están en sus pestañas.',
                )}
            </p>
          </div>

          {showD28dProgressUi ? (
            <D28dProgressDashboard
              compact={false}
              hidePremiacion={isSedeFinal || virtualGymMember || d28dOnly}
              useClassEnrollment={hasD28d || isSedeFinal}
              refreshKey={progressRefreshKey}
              showWeightSnapshot={hasTraining && !isSedeFinal}
            />
          ) : null}
          {showD28dProgressUi ? (
            <D28dAthleteEvolutionChart
              sedeMode={isSedeFinal}
              refreshKey={progressRefreshKey}
            />
          ) : null}
          {showD28dTests ? (
            <div className="card p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h2 className="font-bold text-[var(--d28d-text)] text-sm">{t('mi_progreso.tests_title', 'Pruebas físicas')}</h2>
                <p className="text-xs d28d-text-muted mt-1">
                  {t('mi_progreso.tests_hint_short', 'Registro por ciclo y fotos de progreso en sus pestañas.')}
                </p>
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                <button type="button" className="btn btn-secondary text-sm" onClick={() => setActiveTab('pruebas')}>
                  {t('mi_progreso.open_tests', 'Abrir pruebas')}
                </button>
                <button type="button" className="btn btn-secondary text-sm" onClick={() => setActiveTab('medidas')}>
                  {t('mi_progreso.open_photos', 'Abrir fotos')}
                </button>
              </div>
            </div>
          ) : null}
          {isSedeFinal ? (
            <div className="card p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="font-bold text-[var(--d28d-text)]">{t('mi_progreso.attendance_title', 'Asistencia')}</h2>
                <p className="text-sm d28d-text-muted mt-1">
                  {t('mi_progreso.sede_attendance_hint', 'Consulta el calendario de clases, inscríbete y revisa tus grabaciones.')}
                </p>
              </div>
              <button type="button" className="btn-primary shrink-0" onClick={() => setActiveTab('clases')}>
                {t('mi_progreso.open_live_classes', 'Ver clases e inscribirme')}
              </button>
            </div>
          ) : null}
          {hasTraining ? (
            <SharedProgressSnapshot
              refreshKey={progressRefreshKey}
              onOpenMeasures={() => setActiveTab('medidas')}
              onOpenWellness={hasTraining ? () => setActiveTab('entrenamiento') : undefined}
            />
          ) : null}
          {hasTraining ? <TrainingProgressPanel showContinue onContinue={() => setActiveTab('entrenamiento')} /> : null}
          {hasTraining ? <AthleteTrainingEvolutionChart /> : null}

          {hasTraining && !hasD28d ? (
            <div className="card p-5 flex flex-col gap-3">
              <h2 className="font-bold text-[var(--d28d-text)]">{t('mi_progreso.measures_title', 'Medidas corporales')}</h2>
              <p className="text-sm d28d-text-muted">{t('mi_progreso.measures_training_hint', 'Registra medidas corporales con tu coach en la pestaña Medidas.')}</p>
              <button type="button" className="btn-secondary shrink-0 w-fit" onClick={() => setActiveTab('medidas')}>
                {t('mi_progreso.open_measures', 'Registrar medidas')}
              </button>
            </div>
          ) : null}

          {showD28dExtras ? (
            <div className="space-y-4">
              <div className="card p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h2 className="font-bold text-[var(--d28d-text)]">{t('mi_progreso.attendance_title', 'Asistencia')}</h2>
                  <p className="text-sm d28d-text-muted mt-1">{t('mi_progreso.attendance_hint', 'Resumen de clases a las que has asistido.')}</p>
                </div>
                <button type="button" className="btn-secondary shrink-0" onClick={() => setActiveTab('clases')}>
                  {t('mi_progreso.open_live_classes', 'Ver clases e inscribirme')}
                </button>
              </div>
              <div className="card p-5">
                <h2 className="font-bold text-[var(--d28d-text)] mb-2">
                  {t('d28d.challenges.sidebar', 'Retos y eventos')}
                </h2>
                <p className="text-sm d28d-text-muted mb-4">
                  {t('d28d.challenges.sidebar_hint', 'Participa en eventos del gimnasio virtual cuando estén activos.')}
                </p>
                <D28dChallengesPanel programId={programId} compact />
              </div>
            </div>
          ) : null}

          {hasD28d && !hideD28dBranding ? (
            <div className="card p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="font-bold text-[var(--d28d-text)]">{t('home.gym_virtual', 'Gimnasio Virtual')}</h2>
                <p className="text-sm d28d-text-muted mt-1">{t('mi_progreso.live_classes_hint', 'Consulta el horario, inscríbete y entra al Zoom cuando empiece la sesión.')}</p>
              </div>
              <button type="button" className="btn-primary shrink-0" onClick={() => setActiveTab('clases')}>
                {t('mi_progreso.open_live_classes', 'Ver clases e inscribirme')}
              </button>
            </div>
          ) : null}

          {hasFood && foodExternal ? (
            <div className="card p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="font-bold text-[var(--d28d-text)]">FOOD PLAN</h2>
                <p className="text-sm d28d-text-muted mt-1">{t('mi_progreso.food_summary_hint', 'Resumen nutricional en FOOD PLAN. Abre la plataforma para registrar comidas.')}</p>
              </div>
              <button type="button" className="btn-primary shrink-0" onClick={() => setActiveTab('alimentacion')}>
                {t('mi_progreso.open_food', 'Ir a Food Plan')}
              </button>
            </div>
          ) : null}

          {hasFitLive ? (
            <div className="card p-5 space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                  <h2 className="font-bold text-[var(--d28d-text)]">Fit Live</h2>
                  <p className="text-sm d28d-text-muted mt-1">
                    {t(
                      'mi_progreso.fit_live_hint',
                      'Tu saldo de tokens y reservas. Se actualiza al volver a esta pantalla.',
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  className="btn-secondary shrink-0 text-sm"
                  disabled={fitLiveLoading}
                  onClick={() => setProgressRefreshKey((n) => n + 1)}
                >
                  {fitLiveLoading
                    ? t('mi_progreso.refreshing', 'Actualizando…')
                    : t('mi_progreso.refresh', 'Actualizar')}
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                <div className="rounded-xl border border-[var(--d28d-border)] bg-[var(--d28d-surface-alt,#f9fafb)] px-3 py-2">
                  <div className="text-xs d28d-text-muted uppercase tracking-wide">Saldo</div>
                  <div className="font-semibold text-[var(--d28d-text)] text-lg">
                    {fitLiveSnap?.balance ?? '—'}
                    {' '}
                    tok
                  </div>
                </div>
                <div className="rounded-xl border border-[var(--d28d-border)] bg-[var(--d28d-surface-alt,#f9fafb)] px-3 py-2">
                  <div className="text-xs d28d-text-muted uppercase tracking-wide">Reservas</div>
                  <div className="font-semibold text-[var(--d28d-text)] text-lg">
                    {fitLiveSnap?.reservations ?? '—'}
                  </div>
                </div>
                <div className="rounded-xl border border-[var(--d28d-border)] bg-[var(--d28d-surface-alt,#f9fafb)] px-3 py-2 col-span-2 sm:col-span-1">
                  <div className="text-xs d28d-text-muted uppercase tracking-wide">Activas / asistidas</div>
                  <div className="font-semibold text-[var(--d28d-text)] text-lg">
                    {fitLiveSnap?.active ?? '—'}
                  </div>
                </div>
              </div>
              {onNavigate ? (
                <button
                  type="button"
                  className="btn-primary w-fit text-sm"
                  onClick={() => onNavigate('service:fit-live')}
                >
                  {t('mi_progreso.open_fit_live', 'Ir a Fit Live')}
                </button>
              ) : null}
            </div>
          ) : null}

          {!hasD28d && !hasTraining && !hasFood && !hasFitLive ? (
            <p className="d28d-text-muted">{t('services.no_services', 'Aún no tienes servicios activos.', { brand: brandName || PUBLIC_BRAND_NAME })}</p>
          ) : null}
        </div>
      )}

      {tab === 'alimentacion' && hasFood ? (
        foodExternal ? (
          <div className="card p-6 space-y-4">
            <h2 className="text-lg font-bold text-[var(--d28d-text)]">{t('mi_progreso.tab.alimentacion', 'Alimentación')}</h2>
            <p className="text-sm d28d-text-muted">
              {t('mi_progreso.food_external_hint', 'Tu registro nutricional y asistente viven en la plataforma FOOD PLAN.')}
            </p>
            {hasTraining ? <TrainingFoodSyncBanner /> : null}
            {foodError ? (
              <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" role="alert">
                {foodError}
              </p>
            ) : null}
            <button type="button" className="btn-primary w-fit" disabled={foodLoading} onClick={handleOpenFood}>
                {foodLoading ? t('mi_progreso.connecting', 'Conectando…') : t('mi_progreso.open_food', 'Ir a Food Plan')}
            </button>
          </div>
        ) : (
          <div className="card p-6 space-y-4">
            <h2 className="text-lg font-bold text-[var(--d28d-text)]">{t('mi_progreso.tab.alimentacion', 'Alimentación')}</h2>
            <p className="text-sm d28d-text-muted">{t('mi_progreso.food_legacy_hint', 'Usa Mi Plan y registro de comidas desde el menú FOOD.')}</p>
          </div>
        )
      ) : null}

      {tab === 'entrenamiento' && hasTraining ? (
        trainingExternal ? (
          <div className="card p-6 space-y-4">
            <p className="d28d-text-muted text-sm">
              {t('mi_progreso.training_external_hint', 'Abre tu panel de entrenamiento en la app dedicada.')}
            </p>
            <button
              type="button"
              className="btn-primary"
              onClick={() => openTrainingModule('/dashboard', '/athlete', user)}
            >
              {t('mi_progreso.open_training_dashboard', 'Abrir entrenamiento')}
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            <TrainingModule initialSubView={trainingSubview} />
            <section>
              <h2 className="text-lg font-bold text-[var(--d28d-text)] mb-3">{t('nav.mi_progreso', 'Mi Progreso')}</h2>
              <TrainingProgressPanel />
            </section>
            <div className="card p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-sm d28d-text-muted">
                {t('mi_progreso.measures_training_tab_hint', 'Las medidas corporales están en la pestaña Medidas, separadas de tu rutina.')}
              </p>
              <button type="button" className="btn-secondary shrink-0" onClick={() => setActiveTab('medidas')}>
                {t('mi_progreso.open_measures', 'Registrar medidas')}
              </button>
            </div>
            {user?.trainer_id ? (
              <TrainingCoachCard trainerId={user.trainer_id} />
            ) : null}
            <section>
              <h2 className="text-lg font-bold text-[var(--d28d-text)] mb-3">{t('mi_progreso.tab.habitos_wellness', 'Bienestar')}</h2>
              <TrainingWellnessPanel />
            </section>
          </div>
        )
      ) : null}

      {tab === 'medidas' && (hasTraining || hasD28d) ? (
        <div className="space-y-8">
          {hasTraining ? <TrainingFoodSyncBanner /> : null}
          {hasTraining ? (
            <section>
              <h2 className="text-lg font-bold text-[var(--d28d-text)] mb-3">{t('mi_progreso.tab.measures', 'Medidas corporales')}</h2>
              <TrainingBodyMeasurements embedded />
            </section>
          ) : null}
          {hasD28d ? (
            <section className="card p-5 space-y-3">
              <h2 className="text-lg font-bold text-[var(--d28d-text)]">{t('mi_progreso.tab.fotos', 'Fotos')}</h2>
              <ProgressPhotosPanel variant="slots" apiBase="/d28d/progress-photos" />
            </section>
          ) : null}
        </div>
      ) : null}

      {tab === 'pruebas' && showD28dTests ? (
        <div className="space-y-8">
          <FitnessTestPanel />
          {showD28dPhotos ? (
            <section>
              <h2 className="text-lg font-bold text-[var(--d28d-text)] mb-3">{t('mi_progreso.photos_title', 'Fotos de progreso')}</h2>
              <ProgressPhotosPanel variant="slots" />
            </section>
          ) : null}
        </div>
      ) : null}

      {tab === 'premiacion' && showD28dExtras ? (
        <div className="space-y-4">
          <div className="card p-4 sm:p-5 space-y-1.5">
            <h2 className="font-bold text-[var(--d28d-text)] text-lg m-0">
              {t('d28d.hub.premiacion', 'Premiación')}
            </h2>
            <p className="text-sm d28d-text-muted m-0 leading-relaxed">
              {t('d28d.challenges.sidebar_hint', 'Participa en eventos del gimnasio virtual cuando estén activos.')}
            </p>
          </div>
          <D28dChallengesPanel programId={programId} />
        </div>
      ) : null}

      {tab === 'clases' && (hasD28d || isSedeFinal) ? (
        <LiveClassSchedule programId={programId} sedeMode={isSedeFinal} />
      ) : null}
        </>
      )}
    </div>
  );
}
